import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    FormControlLabel,
    Switch,
    Radio,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { WithoutHeader, DesktopLayout } from 'src/layout';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import useUserStore from 'src/stores/user';
import useSubscriptionsStore from 'src/stores/subscriptions';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useProjectsStore from 'src/stores/projects';
import useCollectionsStore from 'src/stores/collections';
import SubscriptionItem from 'src/widgets/SubscriptionItem';
import { AIIcon } from 'src/icons/AIIcon';
import SubscriptionsService from 'src/services/SubscriptionsService';
import CustomPlanConfigurator from 'src/screens/Payment/components/CustomPlanConfigurator';
import { ROUTES_URL } from 'src/constants/routes';
import ROUTES from 'src/router/routes';

import PeriodField from 'src/screens/Payment/components/PeriodField';
import PaymentMethods from 'src/screens/Payment/components/PaymentMethods';
import Points from 'src/screens/Payment/components/Points';
import TicketField from 'src/components/TicketField';
import getPeriodsInfo from 'src/utils/getPeriodInfo';
import formatNumber from 'src/utils/formatNumber';
import { getWordWithNumber } from 'src/utils';
import Title from 'src/components/Title';
import Book from 'src/icons/Book';
import MapLayer from 'src/icons/MapLayer';
import Peoples from 'src/icons/Peoples';
import Dialog from 'src/icons/Dialog';
import ExpandMoreIcon from 'src/icons/ExpandMore';
import ExpandLessIcon from 'src/icons/ExpandLess';
import { CaseIcon } from 'src/icons/CaseIcon';

import DialogConfirm from './components/DialogConfirm';

const CUSTOM_PLAN_ID = -1;

const MyTariffScene = () => {
    const isMobile = useIsMobileSize();
    const history = useHistory();
    const { current_subscription } = useUserStore(({ current_subscription }) => ({ current_subscription }));

    // Если нет активного тарифа, перенаправляем на страницу оплаты
    useEffect(() => {
        if (!current_subscription || !current_subscription.is_active) {
            history.replace(ROUTES_URL.PAYMENT);
        }
    }, [current_subscription, history]);
    const {
        subscriptions,
        selectedPlan: selectedPlanIdFromStore,
        setSelectedPlan,
    } = useSubscriptionsStore(({ subscriptions, selectedPlan, setSelectedPlan }) => ({
        subscriptions,
        selectedPlan,
        setSelectedPlan,
    }));
    const { activeProject, projects } = useProjectsStore(({ activeProject, projects }) => ({
        activeProject,
        projects,
    }));
    const { privateCollection, getPrivateCollection, publicCollections, getCollections } = useCollectionsStore(
        ({ privateCollection, getPrivateCollection, publicCollections, getCollections }) => ({
            privateCollection,
            getPrivateCollection,
            publicCollections,
            getCollections,
        })
    );

    // Получаем данные как в онбординге - из активного проекта и личной коллекции
    const { chats = [] } = privateCollection || {};
    const { keywords: projectKeywords = [], bundles = [] } = activeProject || {};

    const bundlesList = (bundles || []).map(({ name }) => name).filter(Boolean) as string[];
    const chatsList = (chats || []).map(({ telegram_link }) => telegram_link).filter(Boolean) as string[];
    const keywordsList = (projectKeywords || []).map(({ word }) => word).filter(Boolean) as string[];
    const { enqueueSnackbar } = useSnackbar();

    // Состояние AI анализа для индивидуального тарифа
    const [customPlanAIAnalysis, setCustomPlanAIAnalysis] = useState(true);

    // Состояния для отключенных элементов в параметрах мониторинга
    const [disabledKeywords, setDisabledKeywords] = useState<number[]>([]);
    const [disabledBundles, setDisabledBundles] = useState<number[]>([]);
    const [disabledChats, setDisabledChats] = useState<number[]>([]);

    // Состояние развернутости блока параметров мониторинга
    const [isMonitoringParamsExpanded, setIsMonitoringParamsExpanded] = useState(false);

    // Состояние параметров индивидуального тарифа
    const [customPlanParams, setCustomPlanParams] = useState({
        projects: 1,
        keywords: 0,
        bundles: 0,
        chats: 0,
    });

    // Настройки индивидуального тарифа
    const [customPlanSettings, setCustomPlanSettings] = useState<{
        base_price: number;
        project_price: number;
        keyword_price: number;
        bundle_price: number;
        chat_price: number;
        ai_analysis_price: number;
        color_code?: string;
        custom_description?: string;
    } | null>({
        base_price: 990,
        project_price: 0,
        keyword_price: 200,
        bundle_price: 300,
        chat_price: 150,
        ai_analysis_price: 0,
        color_code: '#0044FF',
        custom_description: undefined,
    });
    const [customPlanSettingsLoaded, setCustomPlanSettingsLoaded] = useState(false);

    // Загружаем личную подборку если нужно
    useEffect(() => {
        if (activeProject?.id && (!privateCollection?.id || privateCollection?.chats === undefined)) {
            getPrivateCollection();
        }
    }, [activeProject?.id, privateCollection?.id, privateCollection?.chats, getPrivateCollection]);

    // Загружаем публичные подборки если нужно
    useEffect(() => {
        if (!publicCollections || publicCollections.length === 0) {
            getCollections();
        }
    }, [publicCollections, getCollections]);

    // Грузим настройки индивидуального тарифа (поверх дефолтов)
    useEffect(() => {
        if (customPlanSettingsLoaded) return;
        SubscriptionsService.getCustomPlanSettings()
            .then(
                (data: {
                    base_price: string | number;
                    project_price?: string | number;
                    keyword_price: string | number;
                    bundle_price: string | number;
                    chat_price: string | number;
                    ai_analysis_price?: string | number;
                    color_code?: string;
                    custom_description?: string;
                }) => {
                    const toNumber = (v: string | number | undefined, def: number) => {
                        const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
                        return Number.isFinite(n) ? n : def;
                    };
                    setCustomPlanSettings({
                        base_price: toNumber(data.base_price, 990),
                        project_price: toNumber(data.project_price, 0),
                        keyword_price: toNumber(data.keyword_price, 0),
                        bundle_price: toNumber(data.bundle_price, 0),
                        chat_price: toNumber(data.chat_price, 0),
                        ai_analysis_price: toNumber(data.ai_analysis_price, 0),
                        color_code: data.color_code || '#0044FF',
                        custom_description: data.custom_description || undefined,
                    });
                }
            )
            .catch(() => {})
            .finally(() => setCustomPlanSettingsLoaded(true));
    }, [customPlanSettingsLoaded]);

    // Получаем данные из ВСЕХ проектов для отображения в параметрах мониторинга
    const projectData = useMemo(() => {
        // Собираем уникальные публичные подборки из всех проектов
        const bundlesMap = new Map<number, any>();
        const bundlesList: any[] = [];
        const keywordsList: any[] = [];
        let totalChats = 0;

        projects.forEach((project) => {
            if ('bundles' in project && project.bundles) {
                // Публичные подборки (уникальные)
                project.bundles.forEach((bundle) => {
                    if (bundle.is_public && !bundlesMap.has(bundle.id)) {
                        bundlesMap.set(bundle.id, bundle);
                        bundlesList.push(bundle);
                    }
                });

                // Чаты из личной подборки проекта
                const privateBundle = project.bundles.find((bundle) => !bundle.is_public);
                if (privateBundle?.chat_count) {
                    totalChats += privateBundle.chat_count;
                }
            }

            // Ключевые слова из всех проектов
            if ('keywords' in project && project.keywords) {
                keywordsList.push(...project.keywords);
            }
        });

        // Для UI используем чаты из активного проекта (для отображения в списке)
        const activeChats = privateCollection?.chats || [];

        return {
            bundlesList: bundlesList,
            keywordsList: keywordsList,
            chatsList: activeChats.filter((c) => c.id !== undefined), // Для UI используем активный проект
            bundlesCount: bundlesList.filter((b) => !disabledBundles.includes(b.id)).length,
            keywordsCount: keywordsList.filter((k) => !disabledKeywords.includes(k.id)).length,
            chatsCount: totalChats, // Используем общее количество из всех проектов
        };
    }, [projects, activeProject, privateCollection, disabledBundles, disabledKeywords, disabledChats]);

    // Подсчитываем параметры для отображения из ВСЕХ проектов
    const projectParams = useMemo(() => {
        return {
            bundles: projectData.bundlesCount,
            keywords: projectData.keywordsCount,
            chats: projectData.chatsCount,
        };
    }, [projectData]);

    // Инициализируем параметры индивидуального тарифа из projectParams (из всех проектов)
    useEffect(() => {
        if (customPlanParams.keywords === 0 && customPlanParams.bundles === 0 && customPlanParams.chats === 0) {
            setCustomPlanParams({
                projects: Math.max(1, projects.length),
                keywords: projectParams.keywords,
                bundles: projectParams.bundles,
                chats: projectParams.chats,
            });
        }
    }, [projectParams.keywords, projectParams.bundles, projectParams.chats, projects.length]);

    // Синхронизируем customPlanParams с projectParams (из всех проектов)
    useEffect(() => {
        setCustomPlanParams((prev) => ({
            ...prev,
            projects: Math.max(prev.projects, projects.length),
            keywords: Math.max(prev.keywords, projectParams.keywords),
            bundles: Math.max(prev.bundles, projectParams.bundles),
            chats: Math.max(prev.chats, projectParams.chats),
        }));
    }, [projectParams.keywords, projectParams.bundles, projectParams.chats, projects.length]);

    // Расчет стоимости индивидуального тарифа
    const calculateCustomPlanPrice = (): number => {
        if (!customPlanSettings) return 0;

        const projectsCost = customPlanSettings.project_price * (customPlanParams.projects - 1);
        const keywordsCost = customPlanParams.keywords * customPlanSettings.keyword_price;
        const bundlesCost = customPlanParams.bundles * customPlanSettings.bundle_price;
        const chatsCost = customPlanParams.chats * customPlanSettings.chat_price;
        const aiAnalysisCost = customPlanAIAnalysis ? customPlanSettings.ai_analysis_price : 0;

        return customPlanSettings.base_price + projectsCost + keywordsCost + bundlesCost + chatsCost + aiAnalysisCost;
    };

    const customPlanPrice = useMemo(
        () => calculateCustomPlanPrice(),
        [
            customPlanSettings,
            customPlanParams.projects,
            customPlanParams.keywords,
            customPlanParams.bundles,
            customPlanParams.chats,
            customPlanAIAnalysis,
        ]
    );

    // Если в подписке/плане задан fixed_price — отдаем его (без проверки названия плана);
    // приоритетно для планов с признаком is_admin_custom
    const currentIndividualFixed = useMemo(() => {
        if (!current_subscription) return null;
        const isAdminCustomPlan = Boolean((current_subscription.plan as any)?.is_admin_custom);
        // Приоритет: fixed_price у подписки, затем у плана; при наличии is_admin_custom допускаем 0 только если нет данных вообще
        const fpSub = Number(current_subscription.fixed_price ?? NaN);
        if (Number.isFinite(fpSub) && fpSub > 0) return fpSub;
        const fpPlan = Number((current_subscription.plan as any)?.fixed_price ?? NaN);
        if (Number.isFinite(fpPlan) && fpPlan > 0) return fpPlan;
        // Если план помечен как админский кастом, но fixed не пришел корректно — вернем null, дальше сработают фолбэки
        return null;
    }, [current_subscription]);

    const baseCustomPrice = customPlanSettings?.base_price ?? null;

    // Fallback: если фикс.цена отсутствует — считаем по лимитам плана и текущим настройкам
    const calcFromPlanLimits = useMemo(() => {
        if (!current_subscription) return null;
        if (!customPlanSettings) return null;
        const p = current_subscription.plan;
        const projects = Math.max(1, Number(p.projects_limit || 1));
        const keywords = Number(p.keywords_limit || 0);
        const bundles = Number(p.bundles_limit || 0);
        const chats = Number(p.chats_limit || 0);
        const ai = !!p.ai_analysis_enabled;
        const projectsCost = customPlanSettings.project_price * (projects - 1);
        const keywordsCost = keywords * customPlanSettings.keyword_price;
        const bundlesCost = bundles * customPlanSettings.bundle_price;
        const chatsCost = chats * customPlanSettings.chat_price;
        const aiCost = ai ? customPlanSettings.ai_analysis_price : 0;
        return customPlanSettings.base_price + projectsCost + keywordsCost + bundlesCost + chatsCost + aiCost;
    }, [current_subscription, customPlanSettings]);

    // Для отображения цены индивидуального тарифа всегда используем пересчитанную цену,
    // чтобы она обновлялась при изменении параметров
    // Фиксированная цена используется только для сравнения при переключении тарифов
    const effectiveCustomPlanPrice = customPlanPrice;

    // Гарантируем положительное значение для индивидуального тарифа
    const minimalCustomBase = typeof baseCustomPrice === 'number' && baseCustomPrice > 0 ? baseCustomPrice : 990;
    // Используем пересчитанную цену (customPlanPrice), fallback только если она равна 0 или меньше минимальной
    // calcFromPlanLimits используется только для отображения текущего тарифа, не для расчета нового
    const effectiveCustomPlanPriceNonZero =
        customPlanPrice > 0
            ? Math.max(customPlanPrice, minimalCustomBase)
            : Math.max(Number(calcFromPlanLimits ?? 0) || 0, minimalCustomBase);

    // Логика рекомендации тарифа
    const recommendedPlanId = useMemo(() => {
        if (!subscriptions.length) return null;

        const recommended = subscriptions
            .filter((plan) => {
                // Проверка доступности тарифа
                if (projectParams.bundles > 0 && plan.bundles_limit < projectParams.bundles) return false;
                if (projectParams.keywords > 0 && plan.keywords_limit < projectParams.keywords) return false;
                if (projectParams.chats > 0 && plan.chats_limit < projectParams.chats) return false;
                return true;
            })
            .sort((a, b) => a.price - b.price)[0];

        return recommended?.id || null;
    }, [subscriptions, projectParams]);

    const hasAnyAvailablePlan = subscriptions.some((plan) => {
        if (projectParams.bundles > 0 && plan.bundles_limit < projectParams.bundles) return false;
        if (projectParams.keywords > 0 && plan.keywords_limit < projectParams.keywords) return false;
        if (projectParams.chats > 0 && plan.chats_limit < projectParams.chats) return false;
        return true;
    });

    const shouldShowCustomPlan =
        !hasAnyAvailablePlan || projectParams.keywords > 0 || projectParams.bundles > 0 || projectParams.chats > 0;

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const { cancelSubscription } = useSubscriptionsStore(({ cancelSubscription }) => ({ cancelSubscription }));

    const {
        isLoading: isCanceling,
        isError,
        refetch: cancelSubscriptionRefetch,
    } = useQuery({
        queryKey: 'cancelPayment',
        queryFn: () => cancelSubscription(),
        onSuccess: () => {
            enqueueSnackbar('Подписка приостановлена', { variant: 'success' });
            setCancelDialogOpen(false);
        },
        enabled: false,
    });

    useEffect(() => {
        if (isError) {
            enqueueSnackbar('Произошла ошибка при отмене подписки', { variant: 'error' });
        }
    }, [isError]);

    const handlePlanClick = (planId: number) => {
        setSelectedPlan(planId);
        history.push(ROUTES_URL.SUBSCRIPTIONS);
    };

    // Состояние показа блоков (null = скрыты, 'payment' = блок оплаты, 'select' = блок выбора тарифа)
    // Если нет подписки, автоматически показываем блок выбора тарифа
    const [activeBlock, setActiveBlock] = useState<'payment' | 'select' | null>(null);

    // Для обратной совместимости
    const showPaymentBlock = activeBlock === 'payment';
    // Показываем блок выбора тарифа, если нет подписки или если явно установлен activeBlock === 'select'
    const showSelectTariffBlock = activeBlock === 'select' || !current_subscription;

    const { promoSale, setSubscriptions } = useSubscriptionsStore(({ promoSale, setSubscriptions }) => ({
        promoSale,
        setSubscriptions,
    }));

    // Грузим список подписок если нужно
    useEffect(() => {
        if (!subscriptions.length) {
            SubscriptionsService.getSubscriptionsList()
                .then((data) => {
                    setSubscriptions(data);
                })
                .catch(() => {});
        }
    }, [subscriptions.length, setSubscriptions]);

    // Форма для оплаты
    const [selectedTariffForPayment, setSelectedTariffForPayment] = useState<number | null>(null);

    const { control, handleSubmit, watch, setValue } = useForm({
        defaultValues: {
            plan_id: current_subscription?.plan.id || CUSTOM_PLAN_ID,
            duration_months: 1,
            paymentMethod: 'UMoney',
            bonus: false,
            promo_code: '',
            auto_renewal: true,
            switch_immediately: true, // Перейти сразу или после окончания
        },
    });

    // Обновляем plan_id при изменении выбранного тарифа
    useEffect(() => {
        if (selectedTariffForPayment !== null) {
            setValue('plan_id', selectedTariffForPayment);
            console.log('Обновлен plan_id в форме:', selectedTariffForPayment);
        }
    }, [selectedTariffForPayment, setValue]);

    const { bonusPoints } = useUserStore(({ bonusPoints }) => ({ bonusPoints }));

    const { mutate: createPayment, isLoading: isCreatePaymentLoading } = useMutation(
        SubscriptionsService.queryKey,
        SubscriptionsService.createPayment,
        {
            onSuccess: (data) => {
                document.location = data.confirmation_url;
            },
            onError: (error: any) => {
                console.error('Ошибка создания платежа:', error);
                console.error('Детали ошибки:', error.response?.data);
                const errorMessage =
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    'Произошла ошибка при создании платежа. Попробуйте повторить позднее';
                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                });
            },
        }
    );

    // Расчет стоимости
    const selectedPlanId = watch('plan_id');
    const isCustomPlanSelected = selectedPlanId === CUSTOM_PLAN_ID;
    const switchImmediately = watch('switch_immediately');

    // Цена для текущего плана пользователя: приоритет fixed из /me, если price=0
    const currentPlanUnitPrice = useMemo(() => {
        if (!current_subscription) return 0;
        const fixedOrPlan = Number(currentIndividualFixed ?? current_subscription.plan.price ?? 0) || 0;
        return fixedOrPlan > 0 ? fixedOrPlan : minimalCustomBase;
    }, [current_subscription, currentIndividualFixed, minimalCustomBase]);

    // Если выбран индивидуальный тариф, используем его цену, иначе берем из subscriptions,
    // а для текущего плана пользователя форсим fixed из /me
    const planPrice = isCustomPlanSelected
        ? effectiveCustomPlanPriceNonZero
        : selectedPlanId === current_subscription?.plan.id
        ? currentPlanUnitPrice
        : subscriptions.find((p) => p.id === selectedPlanId)?.price ?? 0;

    const selectedPlanObject = isCustomPlanSelected
        ? null
        : subscriptions.find((p) => p.id === selectedPlanId) || current_subscription?.plan || null;

    const periods = Object.values(getPeriodsInfo(planPrice));
    const periodId = watch('duration_months');
    const isUseBonus = watch('bonus');
    const period = periods.filter((item) => item.value === periodId)?.[0];
    let total = period?.priceWithSale || 0;
    let promocodeDiff = 0;

    // Console debug: всегда логируем ключевые значения, чтобы отловить кейс 0 ₽
    useEffect(() => {
        const debugPayload = {
            hasSubscription: !!current_subscription,
            planId: current_subscription?.plan?.id,
            planName: current_subscription?.plan?.name,
            fixedPriceSub: current_subscription?.fixed_price,
            fixedPricePlan: (current_subscription?.plan as any)?.fixed_price,
            planPrice: current_subscription?.plan?.price,
            basePrice: customPlanSettings?.base_price,
            calcFromPlanLimits,
            customPlanPrice,
            effectiveCustomPlanPriceNonZero,
            selectedPlanId,
            isCustomPlanSelected,
        };
        // @ts-ignore
        (window as any).__tariffDebug = debugPayload;
        // eslint-disable-next-line no-console
        console.log('[MyTariff DEBUG]', debugPayload);
    }, [
        current_subscription,
        customPlanSettings,
        calcFromPlanLimits,
        customPlanPrice,
        effectiveCustomPlanPriceNonZero,
        selectedPlanId,
        isCustomPlanSelected,
    ]);

    // Расчет разницы при понижении/повышении тарифа
    let tariffDowngradeRefund = 0; // Количество дополнительных дней при понижении тарифа
    let tariffUpgradeCharge = 0; // Доплата при повышении тарифа

    // Определяем, является ли переход понижением или повышением тарифа
    // Используем цену тарифа для сравнения (не fixed_price)
    const currentPlanPrice = current_subscription?.plan.price || 0;
    const newPlanPrice = isCustomPlanSelected
        ? effectiveCustomPlanPriceNonZero
        : selectedPlanObject?.id === current_subscription?.plan.id
        ? currentPlanUnitPrice
        : selectedPlanObject?.price ?? 0;
    const isSwitchingTariff =
        current_subscription && (selectedPlanId !== current_subscription.plan.id || isCustomPlanSelected);
    const isDowngrade = isSwitchingTariff && currentPlanPrice > newPlanPrice;
    const isUpgrade = isSwitchingTariff && currentPlanPrice < newPlanPrice;

    // Расчет разницы при переключении тарифа (независимо от switchImmediately, чтобы показывать корректные значения в обеих опциях)
    if (current_subscription && isSwitchingTariff) {
        // Используем реально оплаченную сумму для расчета проррации
        // На бэкенде используется: fixed_price - used_bonus_points (строка 285 в serializers.py)
        const currentTotalPaid = Number(current_subscription.fixed_price || 0) - Number(current_subscription.used_bonus_points || 0);

        // Расчет стоимости нового периода (со скидкой за длительность)
        const validPeriodId = [1, 3, 6, 12].includes(periodId) ? periodId : 1;
        const newPeriodInfo = getPeriodsInfo(newPlanPrice)[validPeriodId];
        let newPeriodTotalPrice = newPeriodInfo?.priceWithSale || newPlanPrice * validPeriodId;

        // ВАЖНО: Применяем промокод к цене нового периода ДО расчета дельты (как на бэкенде)
        // На бэкенде промокод применяется к price_after_duration до расчета delta_before_discounts
        if (promoSale > 0 && newPeriodTotalPrice > 0) {
            newPeriodTotalPrice = newPeriodTotalPrice * (1 - promoSale / 100);
        }

        // Точный расчет по секундам (как на бэкенде)
        // ВАЖНО: На бэкенде используется paid_at or start_date or end_date (строка 289 в serializers.py)
        const endDate = dayjs(current_subscription.end_date);
        const now = dayjs();
        const startDate = dayjs(current_subscription.paid_at || current_subscription.start_date || current_subscription.end_date);

        // Общее количество секунд в периоде подписки
        const totalSeconds = Math.max(1, endDate.diff(startDate, 'second'));
        // Оставшиеся секунды
        const remainingSeconds = Math.max(0, endDate.diff(now, 'second'));

        // Пропорция оставшихся дней от общего периода
        const ratio = totalSeconds > 0 ? remainingSeconds / totalSeconds : 0;

        // Стоимость оставшихся дней текущего тарифа
        const remainingValue = currentTotalPaid * ratio;

        // Дельта = стоимость нового периода (уже с промокодом) - стоимость оставшихся дней текущего тарифа
        const delta = newPeriodTotalPrice - remainingValue;

        if (isDowngrade && delta <= 0) {
            // При понижении: delta отрицательный, конвертируем разницу в дополнительные дни
            // Кэп: не более 50% от стоимости нового периода
            const refundCap = newPeriodTotalPrice * 0.5;
            const refundValue = Math.min(Math.abs(delta), refundCap);

            // Рассчитываем стоимость одного дня нового тарифа
            const totalDaysInPeriod = validPeriodId * 30;
            const pricePerDay = totalDaysInPeriod > 0 ? newPeriodTotalPrice / totalDaysInPeriod : 0;

            // Конвертируем остаток денег в дополнительные дни (округляем в большую сторону)
            const additionalDays = pricePerDay > 0 ? Math.ceil(refundValue / pricePerDay) : 0;

            // Сохраняем для отображения в интерфейсе
            tariffDowngradeRefund = additionalDays; // Теперь это количество дополнительных дней, а не бонусы
        } else if (isUpgrade && delta > 0) {
            // При повышении: доплата = дельта (полная стоимость нового периода - стоимость оставшихся дней текущего тарифа)
            tariffUpgradeCharge = delta;
        }
    }

    // Если переключение тарифа и переход сразу, корректируем стоимость
    if (isSwitchingTariff && switchImmediately && current_subscription) {
        if (isDowngrade) {
            // При понижении тарифа - ничего не платим, разницу возвращаем баллами
            total = 0;
        } else if (isUpgrade && tariffUpgradeCharge > 0) {
            // При повышении тарифа - платим доплату (полная стоимость нового периода минус пропорциональная часть текущего)
            // Промокод уже учтен в tariffUpgradeCharge (применен к newPeriodTotalPrice до расчета дельты)
            total = tariffUpgradeCharge;
            
            // ВАЖНО: При переключении тарифа бонусы применяются к дельте (как на бэкенде)
            // На бэкенде бонусы ограничены 50% от дельты и применяются к delta_before_discounts
            if (isUseBonus && total > 0) {
                const maxBonus = total * 0.5; // Ограничение 50% как на бэкенде
                const actualBonusToUse = Math.min(bonusPoints, maxBonus);
                total = Math.max(0, total - actualBonusToUse);
            }
        }
        // Если тарифы равны по цене или upgradeCharge <= 0, платим полную стоимость нового периода
    }

    // Применяем промокод только если есть что списывать И это НЕ переключение тарифа с switchImmediately
    // При переключении тарифа промокод уже применен к newPeriodTotalPrice до расчета дельты
    if (promoSale > 0 && total > 0 && !(isSwitchingTariff && switchImmediately)) {
        promocodeDiff = total;
        total = total - (total / 100) * promoSale;
        promocodeDiff = promocodeDiff - total;
    }

    // Применяем бонусы только если есть что списывать И это НЕ переключение тарифа с switchImmediately
    // При переключении тарифа бонусы уже применены выше
    if (isUseBonus && total > 0 && !(isSwitchingTariff && switchImmediately)) {
        total = Math.max(0, total - bonusPoints);
    }

    const handleUpgradeToAnnual = () => {
        if (current_subscription) {
            setSelectedPlan(current_subscription.plan.id);
            setActiveBlock('payment'); // Открываем только блок оплаты
            setValue('plan_id', current_subscription.plan.id);
            setValue('duration_months', 12);
            // Прокрутка к блоку оплаты
            setTimeout(() => {
                const paymentBlock = document.getElementById('payment-block');
                if (paymentBlock) {
                    paymentBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    };

    const onSubmit: SubmitHandler<any> = (data) => {
        // Валидация: убеждаемся, что plan_id установлен
        const planId = data.plan_id || selectedTariffForPayment || selectedPlanId;
        if (!planId) {
            enqueueSnackbar('Пожалуйста, выберите тариф', { variant: 'error' });
            return;
        }

        console.log('Создание платежа:', { planId, data, selectedTariffForPayment, selectedPlanId });
        console.log('Промокод из формы:', data.promo_code);

        const body: any = {
            duration_months: data.duration_months,
            is_recurring: data.auto_renewal,
            use_bonus_points: data.bonus ? bonusPoints : 0,
            switch_immediately: data.switch_immediately, // Переход сразу или после окончания
        };

        // Если выбран индивидуальный тариф
        if (planId === CUSTOM_PLAN_ID) {
            body['custom_plan'] = true;
            // Жестко зажимаем минимумы от текущих параметров проекта
            // Убеждаемся, что все значения являются числами, а не null/undefined
            const safeProjects = Math.max(1, (customPlanParams?.projects ?? 0) || 1);
            const safeKeywords = Math.max((projectParams?.keywords ?? 0) || 0, (customPlanParams?.keywords ?? 0) || 0);
            const safeBundles = Math.max((projectParams?.bundles ?? 0) || 0, (customPlanParams?.bundles ?? 0) || 0);
            const safeChats = Math.max((projectParams?.chats ?? 0) || 0, (customPlanParams?.chats ?? 0) || 0);
            body['projects_count'] = safeProjects;
            body['keywords_count'] = safeKeywords;
            body['bundles_count'] = safeBundles;
            body['chats_count'] = safeChats;
            body['ai_analysis_enabled'] = customPlanAIAnalysis;
            // Передаем безопасную цену (не ниже минимальной), чтобы исключить 0 на бэке
            body['price'] = Math.round(effectiveCustomPlanPriceNonZero);
            console.log('Параметры индивидуального тарифа:', {
                projects_count: safeProjects,
                keywords_count: safeKeywords,
                bundles_count: safeBundles,
                chats_count: safeChats,
                ai_analysis_enabled: customPlanAIAnalysis,
            });
        } else {
            if (!planId || planId === CUSTOM_PLAN_ID) {
                enqueueSnackbar('Пожалуйста, выберите тариф', { variant: 'error' });
                return;
            }
            body['plan_id'] = planId;
            const selected = subscriptions.find((p) => p.id === planId);
        }

        // Добавляем промокод, если он есть и не пустой
        let promoCode = data.promo_code;
        // Если промокод пришел как массив, берем первый элемент
        if (Array.isArray(promoCode)) {
            promoCode = promoCode[0];
        }
        if (promoCode && typeof promoCode === 'string' && promoCode.trim()) {
            const promoCodeUpper = promoCode.trim().toUpperCase();
            body['promo_code'] = promoCodeUpper;
            console.log('Промокод добавлен в body:', promoCodeUpper);
        } else {
            console.log('Промокод не добавлен в body, promoCode:', promoCode);
        }

        console.log('Итоговый body для запроса:', JSON.stringify(body, null, 2));
        console.log('Итоговый body для запроса (объект):', body);
        console.log('Промокод в итоговом body:', body.promo_code);
        createPayment(body);
    };

    const handleCancelSubscription = () => {
        setCancelDialogOpen(true);
    };

    const handleConfirmCancel = () => {
        cancelSubscriptionRefetch();
    };

    const handleSelectOtherTariff = () => {
        // Устанавливаем рекомендуемый тариф по умолчанию, если он есть, иначе индивидуальный
        const defaultPlanId = recommendedPlanId || CUSTOM_PLAN_ID;
        setSelectedTariffForPayment(defaultPlanId);
        setValue('plan_id', defaultPlanId);
        setActiveBlock('select'); // Открываем только блок выбора тарифа
        // Прокрутка к блоку выбора тарифа
        setTimeout(() => {
            const tariffBlock = document.getElementById('select-tariff-block');
            if (tariffBlock) {
                tariffBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    // Функция автоматической корректировки выбранных элементов при смене тарифа
    const adjustSelectedItemsForPlan = React.useCallback(
        (plan: (typeof subscriptions)[0] | null) => {
            if (!plan || plan.id === CUSTOM_PLAN_ID) {
                // Для индивидуального тарифа не корректируем
                return;
            }

            // Получаем исходные списки без учета disabled элементов
            const allBundles = projectData.bundlesList;
            const allKeywords = projectData.keywordsList;
            const allChats = projectData.chatsList.filter((c) => c.id !== undefined);

            // Корректируем подборки: оставляем первые N, остальные отключаем
            const allBundleIds = allBundles.map((b) => b.id);
            if (allBundles.length > plan.bundles_limit) {
                const bundlesToKeep = allBundles.slice(0, plan.bundles_limit).map((b) => b.id);
                const bundlesToDisable = allBundles.slice(plan.bundles_limit).map((b) => b.id);
                setDisabledBundles((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => bundlesToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...bundlesToDisable]));
                });
            } else {
                // Если все подборки помещаются в лимит, включаем все (убираем из disabled)
                setDisabledBundles((prev) => prev.filter((id) => !allBundleIds.includes(id)));
            }

            // Корректируем ключевые слова: оставляем первые N, остальные отключаем
            const allKeywordIds = allKeywords.map((k) => k.id);
            if (allKeywords.length > plan.keywords_limit) {
                const keywordsToKeep = allKeywords.slice(0, plan.keywords_limit).map((k) => k.id);
                const keywordsToDisable = allKeywords.slice(plan.keywords_limit).map((k) => k.id);
                setDisabledKeywords((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => keywordsToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...keywordsToDisable]));
                });
            } else {
                // Если все ключевые слова помещаются в лимит, включаем все (убираем из disabled)
                setDisabledKeywords((prev) => prev.filter((id) => !allKeywordIds.includes(id)));
            }

            // Корректируем чаты: оставляем первые N, остальные отключаем
            const allChatIds = allChats.map((c) => c.id).filter((id): id is number => id !== undefined);
            if (allChats.length > plan.chats_limit) {
                const chatsToKeep = allChats
                    .slice(0, plan.chats_limit)
                    .map((c) => c.id)
                    .filter((id): id is number => id !== undefined);
                const chatsToDisable = allChats
                    .slice(plan.chats_limit)
                    .map((c) => c.id)
                    .filter((id): id is number => id !== undefined);
                setDisabledChats((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => chatsToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...chatsToDisable]));
                });
            } else {
                // Если все чаты помещаются в лимит, включаем все (убираем из disabled)
                setDisabledChats((prev) => prev.filter((id) => !allChatIds.includes(id)));
            }
        },
        [projectData]
    );

    // Автоматически корректируем выбранные элементы при изменении выбранного тарифа
    useEffect(() => {
        if (selectedTariffForPayment && selectedTariffForPayment !== CUSTOM_PLAN_ID && subscriptions.length > 0) {
            const currentPlan = subscriptions.find((p) => p.id === selectedTariffForPayment);
            if (currentPlan) {
                adjustSelectedItemsForPlan(currentPlan);
            }
        }
    }, [selectedTariffForPayment, subscriptions, adjustSelectedItemsForPlan]);

    const handleTariffSelect = (planId: number) => {
        setSelectedTariffForPayment(planId);
        setValue('plan_id', planId);
        // Если выбран индивидуальный тариф, пересчитываем цену
        if (planId === CUSTOM_PLAN_ID) {
            // Цена индивидуального тарифа уже вычисляется в customPlanPrice
            // Просто обновим форму
        }
    };

    const renderContent = () => {
        return (
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <DialogConfirm
                    open={cancelDialogOpen}
                    onClose={() => setCancelDialogOpen(false)}
                    onAccept={handleConfirmCancel}
                    date={
                        current_subscription?.end_date
                            ? new Date(current_subscription.end_date).toLocaleDateString('ru-RU')
                            : ''
                    }
                />
                <Box mb={1.5}>
                    <Title>{current_subscription ? 'Ваш текущий тариф' : 'Выберите тариф'}</Title>
                </Box>

                {current_subscription && (
                    <Box mb={2}>
                        <SubscriptionItem
                            value={current_subscription.plan.id}
                            checkedValue={current_subscription.plan.id}
                            title={current_subscription.plan.name}
                            price={
                                // Для отображения активного тарифа показываем стоимость тарифа (plan.price),
                                // а не сумму оплаты (fixed_price подписки)
                                // fixed_price используется только для индивидуальных тарифов или как fallback
                                (() => {
                                    // Если это индивидуальный тариф, используем расчетную цену или fixed_price плана
                                    const isAdminCustom = (current_subscription.plan as any)?.is_admin_custom;
                                    if (isAdminCustom) {
                                        // Для индивидуальных тарифов проверяем fixed_price плана
                                        const fixedPricePlan = Number(
                                            (current_subscription.plan as any)?.fixed_price ?? 0
                                        );
                                        if (fixedPricePlan > 0) return fixedPricePlan;
                                        // Иначе используем расчетную цену
                                        return effectiveCustomPlanPriceNonZero || minimalCustomBase;
                                    }

                                    // Для обычных тарифов используем plan.price (стоимость тарифа)
                                    // Это правильная цена тарифа, а не сумма оплаты со скидками
                                    const planPrice = Number(current_subscription.plan.price ?? 0);
                                    if (planPrice > 0) return planPrice;

                                    // Fallback: fixed_price плана (если plan.price отсутствует)
                                    const fixedPricePlan = Number((current_subscription.plan as any)?.fixed_price ?? 0);
                                    if (fixedPricePlan > 0) return fixedPricePlan;

                                    // Последний fallback: fixed_price подписки (сумма оплаты)
                                    const fixedPriceSub = Number(current_subscription.fixed_price ?? 0);
                                    if (fixedPriceSub > 0) return fixedPriceSub;

                                    // Если ничего не найдено, используем расчетную цену
                                    return effectiveCustomPlanPriceNonZero || minimalCustomBase;
                                })()
                            }
                            color={'#' + current_subscription.plan.color_code}
                            projects={current_subscription.plan.projects_limit}
                            bundles={current_subscription.plan.bundles_limit}
                            chats={current_subscription.plan.chats_limit}
                            keywords={current_subscription.plan.keywords_limit}
                            ai_analysis_enabled={current_subscription.plan.ai_analysis_enabled}
                            spam_filter_enabled={current_subscription.plan.spam_filter_enabled}
                            telegram_notifications_enabled={current_subscription.plan.telegram_notifications_enabled}
                            leads_export_enabled={current_subscription.plan.leads_export_enabled}
                            setup_service_enabled={current_subscription.plan.setup_service_enabled}
                            description={current_subscription.plan.description}
                            isRecommended={false}
                            disabled={false}
                            isCurrentTariff={true}
                            start_date={current_subscription.start_date}
                            end_date={current_subscription.end_date}
                            is_active={current_subscription.is_active}
                            is_recurring={current_subscription.is_recurring}
                            duration_months={current_subscription.duration_months}
                            onUpgradeToAnnual={handleUpgradeToAnnual}
                            onCancelSubscription={handleCancelSubscription}
                            onSelectOtherTariff={handleSelectOtherTariff}
                            isCanceling={isCanceling}
                            onChange={(val) => handlePlanClick(Number(val))}
                        />
                    </Box>
                )}

                {/* Блок оплаты (появляется при нажатии на "Перейти на годовой план") */}
                {showPaymentBlock && current_subscription && (
                    <Box
                        id="payment-block"
                        mt={4}
                    >
                        {/* Визуальный разделитель */}
                        <Box
                            sx={{
                                height: '1px',
                                backgroundColor: '#E3ECF3',
                                mb: 4,
                                width: '100%',
                            }}
                        />

                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Настройки оплаты */}
                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Настройки оплаты
                                </Typography>

                                <Box mb={2.5}>
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                        Период
                                    </Typography>
                                    <PeriodField
                                        periods={periods}
                                        control={control}
                                        name="duration_months"
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                        Способ оплаты
                                    </Typography>
                                    <PaymentMethods
                                        name="paymentMethod"
                                        control={control}
                                        methods={[
                                            {
                                                name: 'Ю Money',
                                                value: 'UMoney',
                                                currency: 'RUB',
                                            },
                                        ]}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <Points
                                        name="bonus"
                                        control={control}
                                        points={bonusPoints}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <TicketField
                                        name="promo_code"
                                        control={control}
                                        placeholder="Промокод"
                                    />
                                </Box>
                            </Box>

                            {/* Визуальный разделитель */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Итоговая сумма */}
                            <Box mb={3}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    К оплате
                                </Typography>
                                <Box
                                    border="1px solid #E3ECF3"
                                    bgcolor="#fff"
                                    borderRadius="16px"
                                    sx={{ overflow: 'hidden' }}
                                >
                                    {isUseBonus && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Списано бонусов
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{bonusPoints} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {promoSale > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Скидка по промокоду
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{formatNumber(Math.round(promocodeDiff))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {isDowngrade && switchImmediately && tariffDowngradeRefund > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                            sx={{
                                                backgroundColor: 'rgba(74, 226, 135, 0.1)',
                                            }}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                Дополнительные дни за оставшиеся дни
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                +{getWordWithNumber(tariffDowngradeRefund, ['день', 'дня', 'дней'])}
                                            </Typography>
                                        </Box>
                                    )}
                                    {isUpgrade &&
                                        switchImmediately &&
                                        current_subscription &&
                                        period &&
                                        tariffUpgradeCharge > 0 && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                px={2}
                                                py={1}
                                                sx={{
                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                }}
                                            >
                                                <Typography
                                                    color="primary"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Учтена стоимость оставшихся дней текущего тарифа
                                                </Typography>
                                                <Typography
                                                    color="primary"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    -
                                                    {formatNumber(
                                                        Math.round(period.priceWithSale - tariffUpgradeCharge)
                                                    )}{' '}
                                                    ₽
                                                </Typography>
                                            </Box>
                                        )}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={2}
                                        sx={{
                                            borderTop: '1px solid #E3ECF3',
                                        }}
                                    >
                                        <Typography
                                            color="primary"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 20,
                                            }}
                                        >
                                            Итого
                                        </Typography>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ minHeight: '32px' }}
                                        >
                                            {!isCustomPlanSelected &&
                                                selectedPlanObject &&
                                                !isDowngrade &&
                                                !isUpgrade &&
                                                total < selectedPlanObject.price * periodId && (
                                                    <Typography
                                                        variant="body1"
                                                        color="text.disabled"
                                                        sx={{ textDecoration: 'line-through', opacity: 0.7 }}
                                                    >
                                                        {formatNumber(selectedPlanObject.price * periodId)} ₽
                                                    </Typography>
                                                )}
                                            {isDowngrade && switchImmediately ? (
                                                <Typography
                                                    color="primary"
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: 20,
                                                        color: '#4AE287',
                                                    }}
                                                >
                                                    Бесплатно
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    color="primary"
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: 20,
                                                    }}
                                                >
                                                    {formatNumber(total)} ₽
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Выбор момента перехода на новый тариф (только если есть текущая подписка и выбран другой тариф) */}
                            {current_subscription &&
                                (selectedPlanId !== current_subscription.plan.id || isCustomPlanSelected) && (
                                    <Box mb={3}>
                                        <Typography
                                            variant="body1"
                                            color="primary"
                                            sx={{ fontWeight: 600, mb: 1 }}
                                        >
                                            Когда перейти на новый тариф?
                                        </Typography>
                                        <Controller
                                            name="switch_immediately"
                                            control={control}
                                            render={({ field }) => (
                                                <Box
                                                    sx={{
                                                        border: '1px solid #E3ECF3',
                                                        borderRadius: '16px',
                                                        backgroundColor: '#fff',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                checked={field.value === true}
                                                                onChange={() => field.onChange(true)}
                                                                value={true}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Перейти сейчас
                                                                </Typography>
                                                                {isDowngrade && tariffDowngradeRefund > 0 && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', mt: 0.5 }}
                                                                    >
                                                                        Вы получите +
                                                                        {getWordWithNumber(tariffDowngradeRefund, [
                                                                            'день',
                                                                            'дня',
                                                                            'дней',
                                                                        ])}{' '}
                                                                        сервиса за оставшиеся дни
                                                                    </Typography>
                                                                )}
                                                                {isUpgrade &&
                                                                    switchImmediately &&
                                                                    tariffUpgradeCharge > 0 && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: 'rgba(0, 0, 0, 0.6)',
                                                                                mt: 0.5,
                                                                            }}
                                                                        >
                                                                            Стоимость оставшихся дней текущего тарифа
                                                                            будет учтена
                                                                        </Typography>
                                                                    )}
                                                            </Box>
                                                        }
                                                        sx={{
                                                            px: 2,
                                                            py: 1.5,
                                                            width: '100%',
                                                            m: 0,
                                                            borderBottom: '1px solid #E3ECF3',
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                checked={field.value === false}
                                                                onChange={() => field.onChange(false)}
                                                                value={false}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Перейти после окончания текущего тарифа
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: 'rgba(0, 0, 0, 0.6)', mt: 0.5 }}
                                                                >
                                                                    Текущий тариф действует до{' '}
                                                                    {dayjs(current_subscription.end_date).format(
                                                                        'DD.MM.YYYY'
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        sx={{ px: 2, py: 1.5, width: '100%', m: 0 }}
                                                    />
                                                </Box>
                                            )}
                                        />
                                    </Box>
                                )}

                            {/* Автоматическое продление */}
                            <Box
                                mt={4}
                                mb={3}
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #E3ECF3',
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name="auto_renewal"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    color="primary"
                                                />
                                            )}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                color="primary"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Автоматическое продление тарифа
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                            >
                                                При включении автопродления вы получите 10% бонусами сразу после оплаты
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            {watch('auto_renewal') && (
                                <Box
                                    mt={2}
                                    mb={3}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: '#4AE287',
                                                opacity: 0.2,
                                                borderRadius: 4,
                                            }}
                                        />
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#000000',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                variant="body1"
                                            >
                                                💸 Вы получите {formatNumber(Math.round(total * 0.1))} ₽ бонусами сразу
                                                после оплаты
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    disabled={isCreatePaymentLoading}
                                >
                                    {showPaymentBlock
                                        ? `Перейти на годовой тариф ${current_subscription.plan.name}`
                                        : isDowngrade && switchImmediately
                                        ? 'Перейти на новый тариф'
                                        : 'Перейти к оплате'}
                                </Button>
                                {watch('auto_renewal') && (
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                                    >
                                        Автопродление можно будет отключить в разделе «Ваш тариф»
                                    </Typography>
                                )}
                            </Box>
                        </form>
                    </Box>
                )}

                {/* Блок выбора другого тарифа (появляется при нажатии на "Выбрать другой тариф") */}
                {showSelectTariffBlock && (
                    <Box
                        id="select-tariff-block"
                        mt={4}
                    >
                        {/* Визуальный разделитель */}
                        <Box
                            sx={{
                                height: '1px',
                                backgroundColor: '#E3ECF3',
                                mb: 4,
                                width: '100%',
                            }}
                        />

                        {/* Параметры мониторинга */}
                        <Box mb={3}>
                            <Accordion
                                expanded={isMonitoringParamsExpanded}
                                onChange={(_, expanded) => setIsMonitoringParamsExpanded(expanded)}
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    '&:before': { display: 'none' },
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={isMonitoringParamsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{
                                        px: 0,
                                        minHeight: 'auto',
                                        '& .MuiAccordionSummary-content': {
                                            margin: '0 !important',
                                        },
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            color: 'primary.main',
                                            '& svg': {
                                                width: '24px',
                                                height: '24px',
                                            },
                                        },
                                        '&.Mui-expanded': {
                                            minHeight: 'auto',
                                        },
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        width="100%"
                                        pr={2}
                                    >
                                        <Typography
                                            variant="h5"
                                            color="primary"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 24,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Параметры мониторинга
                                        </Typography>
                                        {!isMonitoringParamsExpanded && (
                                            <Box
                                                display="flex"
                                                gap={1.5}
                                                alignItems="center"
                                            >
                                                {projectData.keywordsList.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={getWordWithNumber(projectParams.keywords, [
                                                            'ключевое слово',
                                                            'ключевых слова',
                                                            'ключевых слов',
                                                        ])}
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                                {projectData.bundlesList.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={getWordWithNumber(projectParams.bundles, [
                                                            'подборка',
                                                            'подборки',
                                                            'подборок',
                                                        ])}
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                                {projectData.chatsList.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={getWordWithNumber(projectParams.chats, [
                                                            'чат',
                                                            'чата',
                                                            'чатов',
                                                        ])}
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 0, pb: 0 }}>
                                    <Box
                                        sx={{
                                            maxHeight: isMonitoringParamsExpanded ? '600px' : 'none',
                                            overflowY: 'auto',
                                            pr: 1,
                                            '&::-webkit-scrollbar': {
                                                width: '6px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: '#f1f1f1',
                                                borderRadius: '3px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: '#c1c1c1',
                                                borderRadius: '3px',
                                            },
                                        }}
                                    >
                                        {/* Ключевые слова */}
                                        {projectData.keywordsList.length > 0 && (
                                            <Box mb={2}>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mb={1.5}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        color="primary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Ключевые слова:
                                                    </Typography>
                                                    {projectData.keywordsList.length > 0 && (
                                                        <Chip
                                                            color="primary"
                                                            variant={
                                                                projectParams.keywords ===
                                                                projectData.keywordsList.length
                                                                    ? 'filled'
                                                                    : 'outlined'
                                                            }
                                                            label={`${projectParams.keywords} из ${projectData.keywordsList.length}`}
                                                            sx={{
                                                                background:
                                                                    projectParams.keywords !==
                                                                        projectData.keywordsList.length && !isMobile
                                                                        ? '#fff'
                                                                        : null,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <List
                                                    style={{
                                                        border: '1px solid',
                                                        borderColor: '#E3ECF3',
                                                        borderRadius: '16px',
                                                        backgroundColor: '#fff',
                                                        width: '100%',
                                                    }}
                                                    disablePadding
                                                >
                                                    {projectData.keywordsList.map((keyword, idx) => {
                                                        const id = keyword.id;
                                                        const name = keyword.word as string;
                                                        const isSelected = !disabledKeywords.includes(id);
                                                        const rawAI = activeProject?.niche_analysis_keywords;
                                                        const aiKeywords: string[] = Array.isArray(rawAI) ? rawAI : [];
                                                        const isAIRecommended = name
                                                            ? aiKeywords.includes(name)
                                                            : false;

                                                        // Определяем, будет ли элемент неактивен при выбранном тарифе
                                                        const selectedPlan = subscriptions.find(
                                                            (p) => p.id === selectedTariffForPayment
                                                        );
                                                        const willBeInactive =
                                                            selectedPlan && selectedPlan.id !== CUSTOM_PLAN_ID
                                                                ? projectData.keywordsList.length >
                                                                      selectedPlan.keywords_limit &&
                                                                  idx >= selectedPlan.keywords_limit
                                                                : false;

                                                        return (
                                                            <ListItem
                                                                key={id}
                                                                divider={projectData.keywordsList.length - 1 !== idx}
                                                                sx={{
                                                                    opacity: willBeInactive ? 0.5 : 1,
                                                                    backgroundColor: willBeInactive
                                                                        ? '#f5f5f5'
                                                                        : 'transparent',
                                                                }}
                                                                secondaryAction={
                                                                    <Switch
                                                                        checked={isSelected && !willBeInactive}
                                                                        disabled={willBeInactive}
                                                                        onChange={() => {
                                                                            if (!willBeInactive) {
                                                                                setDisabledKeywords((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, id]
                                                                                        : prev.filter((k) => k !== id)
                                                                                );
                                                                            }
                                                                        }}
                                                                        color="primary"
                                                                    />
                                                                }
                                                            >
                                                                <ListItemIcon>
                                                                    <Box
                                                                        sx={{
                                                                            '& svg': {
                                                                                width: '20px',
                                                                                height: '20px',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Book />
                                                                    </Box>
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            <Typography variant="body1">
                                                                                {name}
                                                                            </Typography>
                                                                            {isAIRecommended && (
                                                                                <Chip
                                                                                    icon={
                                                                                        <AIIcon
                                                                                            width={14}
                                                                                            height={14}
                                                                                        />
                                                                                    }
                                                                                    label="ИИ рекомендует"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor:
                                                                                            'rgba(122, 48, 228, 0.1)',
                                                                                        color: '#7A30E4',
                                                                                        fontWeight: 600,
                                                                                        border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                                        '& .MuiChip-icon': {
                                                                                            color: '#7A30E4',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {willBeInactive && (
                                                                                <Chip
                                                                                    label="Неактивно"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: '#ffebee',
                                                                                        color: '#c62828',
                                                                                        fontWeight: 600,
                                                                                        border: '1px solid #ef9a9a',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        )}

                                        {/* Подборки */}
                                        {projectData.bundlesList.length > 0 && (
                                            <Box mb={2}>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mb={1.5}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        color="primary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Подборки чатов:
                                                    </Typography>
                                                    <Chip
                                                        color="primary"
                                                        variant={
                                                            projectParams.bundles === projectData.bundlesList.length
                                                                ? 'filled'
                                                                : 'outlined'
                                                        }
                                                        label={`${projectParams.bundles} из ${projectData.bundlesList.length}`}
                                                        sx={{
                                                            background:
                                                                projectParams.bundles !==
                                                                    projectData.bundlesList.length && !isMobile
                                                                    ? '#fff'
                                                                    : null,
                                                        }}
                                                    />
                                                </Box>

                                                <List
                                                    sx={{
                                                        width: '100%',
                                                        background: '#fff',
                                                        border: '1px solid',
                                                        borderColor: '#E3ECF3',
                                                        display: 'block',
                                                        borderRadius: '16px',
                                                    }}
                                                    disablePadding
                                                >
                                                    {projectData.bundlesList.map((bundle, idx) => {
                                                        const isSelected = !disabledBundles.includes(bundle.id);
                                                        const recommendedCollectionIds =
                                                            activeProject?.recommended_collections || [];
                                                        const isAIRecommended = recommendedCollectionIds.includes(
                                                            bundle.id
                                                        );
                                                        const bundleDetail = publicCollections?.find(
                                                            (c) => c.id === bundle.id
                                                        );

                                                        // Определяем, будет ли элемент неактивен при выбранном тарифе
                                                        const selectedPlan = subscriptions.find(
                                                            (p) => p.id === selectedTariffForPayment
                                                        );
                                                        const willBeInactive =
                                                            selectedPlan && selectedPlan.id !== CUSTOM_PLAN_ID
                                                                ? projectData.bundlesList.length >
                                                                      selectedPlan.bundles_limit &&
                                                                  idx >= selectedPlan.bundles_limit
                                                                : false;

                                                        return (
                                                            <ListItem
                                                                key={bundle.id}
                                                                divider={idx !== projectData.bundlesList.length - 1}
                                                                sx={{
                                                                    opacity: willBeInactive ? 0.5 : 1,
                                                                    backgroundColor: willBeInactive
                                                                        ? '#f5f5f5'
                                                                        : 'transparent',
                                                                }}
                                                                secondaryAction={
                                                                    <Switch
                                                                        checked={isSelected && !willBeInactive}
                                                                        disabled={willBeInactive}
                                                                        onChange={() => {
                                                                            if (!willBeInactive) {
                                                                                setDisabledBundles((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, bundle.id]
                                                                                        : prev.filter(
                                                                                              (id) => id !== bundle.id
                                                                                          )
                                                                                );
                                                                            }
                                                                        }}
                                                                        color="primary"
                                                                    />
                                                                }
                                                            >
                                                                <ListItemIcon>
                                                                    <MapLayer />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            <Typography
                                                                                variant="body1"
                                                                                color="primary"
                                                                            >
                                                                                {bundleDetail?.name ||
                                                                                    bundle.name ||
                                                                                    `Подборка ${bundle.id}`}
                                                                            </Typography>
                                                                            {isAIRecommended && (
                                                                                <Chip
                                                                                    icon={
                                                                                        <AIIcon
                                                                                            width={14}
                                                                                            height={14}
                                                                                        />
                                                                                    }
                                                                                    label="ИИ рекомендует"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor:
                                                                                            'rgba(122, 48, 228, 0.1)',
                                                                                        color: '#7A30E4',
                                                                                        fontWeight: 600,
                                                                                        border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                                        '& .MuiChip-icon': {
                                                                                            color: '#7A30E4',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {willBeInactive && (
                                                                                <Chip
                                                                                    label="Неактивно"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: '#ffebee',
                                                                                        color: '#c62828',
                                                                                        fontWeight: 600,
                                                                                        border: '1px solid #ef9a9a',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                    secondary={
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            {(bundleDetail?.city ||
                                                                                bundleDetail?.location) && (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="primary"
                                                                                    sx={{
                                                                                        fontWeight: 500,
                                                                                        backgroundColor:
                                                                                            'rgba(0, 136, 204, 0.1)',
                                                                                        padding: '2px 8px',
                                                                                        borderRadius: '12px',
                                                                                        border: '1px solid rgba(0, 136, 204, 0.2)',
                                                                                    }}
                                                                                >
                                                                                    {bundleDetail?.city ||
                                                                                        bundleDetail?.location}
                                                                                </Typography>
                                                                            )}
                                                                            {(bundleDetail?.chat_count ??
                                                                                bundle.chat_count) !== undefined && (
                                                                                <Box
                                                                                    display="flex"
                                                                                    alignItems="center"
                                                                                    gap={1}
                                                                                >
                                                                                    <Peoples />
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color="primary"
                                                                                    >
                                                                                        {getWordWithNumber(
                                                                                            (bundleDetail?.chat_count ??
                                                                                                bundle.chat_count) as number,
                                                                                            ['чат', 'чата', 'чатов']
                                                                                        )}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        )}

                                        {/* Чаты */}
                                        {projectData.chatsList.length > 0 && (
                                            <Box mb={2}>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mb={1.5}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        color="primary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Добавленные вами чаты:
                                                    </Typography>
                                                    <Chip
                                                        color="primary"
                                                        variant={
                                                            projectParams.chats === projectData.chatsList.length
                                                                ? 'filled'
                                                                : 'outlined'
                                                        }
                                                        label={`${projectParams.chats} из ${projectData.chatsList.length}`}
                                                        sx={{
                                                            background:
                                                                projectParams.chats !== projectData.chatsList.length &&
                                                                !isMobile
                                                                    ? '#fff'
                                                                    : null,
                                                        }}
                                                    />
                                                </Box>
                                                <List
                                                    sx={{
                                                        width: '100%',
                                                        background: '#fff',
                                                        border: '1px solid',
                                                        borderColor: '#E3ECF3',
                                                        display: 'block',
                                                        borderRadius: '16px',
                                                    }}
                                                    disablePadding
                                                >
                                                    {projectData.chatsList.map((chat, idx) => {
                                                        const chatId = chat.id;
                                                        if (chatId === undefined) return null;
                                                        const isSelected = !disabledChats.includes(chatId);
                                                        const chatName =
                                                            chat.name || chat.telegram_link || `Чат ${chatId}`;

                                                        // Определяем, будет ли элемент неактивен при выбранном тарифе
                                                        const selectedPlan = subscriptions.find(
                                                            (p) => p.id === selectedTariffForPayment
                                                        );
                                                        const willBeInactive =
                                                            selectedPlan && selectedPlan.id !== CUSTOM_PLAN_ID
                                                                ? projectData.chatsList.length >
                                                                      selectedPlan.chats_limit &&
                                                                  idx >= selectedPlan.chats_limit
                                                                : false;

                                                        return (
                                                            <ListItem
                                                                key={chatId}
                                                                divider={idx !== projectData.chatsList.length - 1}
                                                                sx={{
                                                                    opacity: willBeInactive ? 0.5 : 1,
                                                                    backgroundColor: willBeInactive
                                                                        ? '#f5f5f5'
                                                                        : 'transparent',
                                                                }}
                                                                secondaryAction={
                                                                    <Switch
                                                                        checked={isSelected && !willBeInactive}
                                                                        disabled={willBeInactive}
                                                                        onChange={() => {
                                                                            if (!willBeInactive) {
                                                                                setDisabledChats((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, chatId]
                                                                                        : prev.filter(
                                                                                              (id) => id !== chatId
                                                                                          )
                                                                                );
                                                                            }
                                                                        }}
                                                                        color="primary"
                                                                    />
                                                                }
                                                            >
                                                                <ListItemIcon>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            '& svg': {
                                                                                width: '24px',
                                                                                height: '24px',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Dialog />
                                                                    </Box>
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            <Typography
                                                                                variant="body1"
                                                                                color="primary"
                                                                            >
                                                                                {chatName}
                                                                            </Typography>
                                                                            {willBeInactive && (
                                                                                <Chip
                                                                                    label="Неактивно"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: '#ffebee',
                                                                                        color: '#c62828',
                                                                                        fontWeight: 600,
                                                                                        border: '1px solid #ef9a9a',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>

                        {/* Визуальный разделитель между параметрами мониторинга и выбором тарифа */}
                        <Box
                            sx={{
                                height: '1px',
                                backgroundColor: '#E3ECF3',
                                my: 4,
                                width: '100%',
                            }}
                        />

                        {/* Список тарифов */}
                        <Box mb={4}>
                            <Typography
                                variant="h5"
                                color="primary"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: 24,
                                    mb: 2.5,
                                    lineHeight: 1.2,
                                }}
                            >
                                Выберите тариф
                            </Typography>
                            <Box
                                display="grid"
                                gridTemplateColumns={isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))'}
                                gap={2}
                                sx={{
                                    alignItems: 'start',
                                }}
                            >
                                {subscriptions
                                    .sort((a, b) => {
                                        if (+a.price < +b.price) return -1;
                                        if (+a.price > +b.price) return 1;
                                        return 0;
                                    })
                                    .map((plan) => {
                                        const isRecommended = plan.id === recommendedPlanId;
                                        // Разрешаем выбор любого тарифа (включая более слабые)
                                        // Неактивные элементы будут показаны в параметрах мониторинга
                                        const isPlanAvailable = () => {
                                            return true; // Всегда разрешаем выбор
                                        };

                                        const getPlanUnavailableReason = (): string | null => {
                                            // Больше не показываем причину недоступности, так как разрешаем выбор
                                            return null;
                                        };

                                        // Определяем, какие элементы будут неактивны при выборе этого тарифа
                                        const getInactiveItemsInfo = () => {
                                            const inactive: {
                                                type: 'keywords' | 'bundles' | 'chats';
                                                count: number;
                                                limit: number;
                                            }[] = [];

                                            if (projectData.keywordsList.length > plan.keywords_limit) {
                                                inactive.push({
                                                    type: 'keywords',
                                                    count: projectData.keywordsList.length - plan.keywords_limit,
                                                    limit: plan.keywords_limit,
                                                });
                                            }

                                            if (projectData.bundlesList.length > plan.bundles_limit) {
                                                inactive.push({
                                                    type: 'bundles',
                                                    count: projectData.bundlesList.length - plan.bundles_limit,
                                                    limit: plan.bundles_limit,
                                                });
                                            }

                                            if (projectData.chatsList.length > plan.chats_limit) {
                                                inactive.push({
                                                    type: 'chats',
                                                    count: projectData.chatsList.length - plan.chats_limit,
                                                    limit: plan.chats_limit,
                                                });
                                            }

                                            return inactive;
                                        };

                                        const isSelected = selectedTariffForPayment === plan.id;

                                        return (
                                            <Box
                                                key={plan.id}
                                                sx={{ position: 'relative', height: '100%' }}
                                            >
                                                {isRecommended && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -12,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            zIndex: 1,
                                                            pointerEvents: 'none',
                                                        }}
                                                    >
                                                        <Chip
                                                            icon={
                                                                <AIIcon
                                                                    width={14}
                                                                    height={14}
                                                                />
                                                            }
                                                            label="ИИ рекомендует"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                                color: '#7A30E4',
                                                                fontWeight: 600,
                                                                border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                '& .MuiChip-icon': {
                                                                    color: '#7A30E4',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                <SubscriptionItem
                                                    value={plan.id}
                                                    checkedValue={selectedTariffForPayment || plan.id}
                                                    title={plan.name}
                                                    price={plan.price}
                                                    color={'#' + plan.color_code}
                                                    projects={plan.projects_limit}
                                                    bundles={plan.bundles_limit}
                                                    chats={plan.chats_limit}
                                                    keywords={plan.keywords_limit}
                                                    ai_analysis_enabled={plan.ai_analysis_enabled}
                                                    spam_filter_enabled={plan.spam_filter_enabled}
                                                    telegram_notifications_enabled={plan.telegram_notifications_enabled}
                                                    leads_export_enabled={plan.leads_export_enabled}
                                                    setup_service_enabled={plan.setup_service_enabled}
                                                    description={plan.description}
                                                    isRecommended={isRecommended}
                                                    disabled={false}
                                                    onChange={(val) => handleTariffSelect(Number(val))}
                                                />
                                            </Box>
                                        );
                                    })}

                                {/* Индивидуальный тариф */}
                                {shouldShowCustomPlan && (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            height: '100%',
                                            gridColumn: isMobile ? '1' : '1 / -1',
                                        }}
                                    >
                                        <SubscriptionItem
                                            value={CUSTOM_PLAN_ID}
                                            checkedValue={selectedTariffForPayment || CUSTOM_PLAN_ID}
                                            title="Индивидуальный"
                                            price={effectiveCustomPlanPriceNonZero}
                                            color={customPlanSettings?.color_code || '#0044FF'}
                                            projects={customPlanParams.projects}
                                            bundles={customPlanParams.bundles}
                                            chats={customPlanParams.chats}
                                            keywords={customPlanParams.keywords}
                                            ai_analysis_enabled={false}
                                            spam_filter_enabled={true}
                                            telegram_notifications_enabled={true}
                                            leads_export_enabled={true}
                                            setup_service_enabled={false}
                                            description={
                                                customPlanSettings?.custom_description ||
                                                'Тариф формируется на основе выбранных вами параметров. Все опции доступны, кроме настройки под ключ.'
                                            }
                                            isRecommended={false}
                                            disabled={false}
                                            isCustomPlan={true}
                                            customPlanAIAnalysis={customPlanAIAnalysis}
                                            onCustomPlanAIAnalysisChange={setCustomPlanAIAnalysis}
                                            aiAnalysisPrice={customPlanSettings?.ai_analysis_price || 0}
                                            customPlanConfigurator={
                                                customPlanSettings ? (
                                                    <CustomPlanConfigurator
                                                        projects={customPlanParams.projects}
                                                        keywords={customPlanParams.keywords}
                                                        bundles={customPlanParams.bundles}
                                                        chats={customPlanParams.chats}
                                                        minKeywords={projectParams.keywords}
                                                        minBundles={projectParams.bundles}
                                                        minChats={projectParams.chats}
                                                        onProjectsChange={(value) => {
                                                            setCustomPlanParams((prev) => ({
                                                                ...prev,
                                                                projects: value,
                                                            }));
                                                        }}
                                                        onKeywordsChange={(value) => {
                                                            setCustomPlanParams((prev) => ({
                                                                ...prev,
                                                                keywords: value,
                                                            }));
                                                        }}
                                                        onBundlesChange={(value) => {
                                                            setCustomPlanParams((prev) => ({
                                                                ...prev,
                                                                bundles: value,
                                                            }));
                                                        }}
                                                        onChatsChange={(value) => {
                                                            setCustomPlanParams((prev) => ({ ...prev, chats: value }));
                                                        }}
                                                        aiAnalysisEnabled={customPlanAIAnalysis}
                                                        onAIAnalysisChange={setCustomPlanAIAnalysis}
                                                        aiAnalysisPrice={customPlanSettings.ai_analysis_price}
                                                        leadsExportEnabled={true}
                                                        onLeadsExportChange={() => {}}
                                                        projectPrice={customPlanSettings.project_price}
                                                        keywordPrice={customPlanSettings.keyword_price}
                                                        bundlePrice={customPlanSettings.bundle_price}
                                                        chatPrice={customPlanSettings.chat_price}
                                                        basePrice={customPlanSettings.base_price}
                                                    />
                                                ) : null
                                            }
                                            onChange={(val) => handleTariffSelect(Number(val))}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Настройки оплаты */}
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Настройки оплаты
                                </Typography>

                                <Box mb={2.5}>
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                        Период
                                    </Typography>
                                    <PeriodField
                                        periods={periods}
                                        control={control}
                                        name="duration_months"
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                        Способ оплаты
                                    </Typography>
                                    <PaymentMethods
                                        name="paymentMethod"
                                        control={control}
                                        methods={[
                                            {
                                                name: 'Ю Money',
                                                value: 'UMoney',
                                                currency: 'RUB',
                                            },
                                        ]}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <Points
                                        name="bonus"
                                        control={control}
                                        points={bonusPoints}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <TicketField
                                        name="promo_code"
                                        control={control}
                                        placeholder="Промокод"
                                    />
                                </Box>
                            </Box>

                            {/* Визуальный разделитель */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Итоговая сумма */}
                            <Box mb={3}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    К оплате
                                </Typography>
                                <Box
                                    border="1px solid #E3ECF3"
                                    bgcolor="#fff"
                                    borderRadius="16px"
                                    sx={{ overflow: 'hidden' }}
                                >
                                    {isUseBonus && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Списано бонусов
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{bonusPoints} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {promoSale > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Скидка по промокоду
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{formatNumber(Math.round(promocodeDiff))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {isDowngrade && switchImmediately && tariffDowngradeRefund > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                            sx={{
                                                backgroundColor: 'rgba(74, 226, 135, 0.1)',
                                            }}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                Дополнительные дни за оставшиеся дни
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                +{getWordWithNumber(tariffDowngradeRefund, ['день', 'дня', 'дней'])}
                                            </Typography>
                                        </Box>
                                    )}
                                    {isUpgrade &&
                                        switchImmediately &&
                                        current_subscription &&
                                        period &&
                                        tariffUpgradeCharge > 0 && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                px={2}
                                                py={1}
                                                sx={{
                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                }}
                                            >
                                                <Typography
                                                    color="primary"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Учтена стоимость оставшихся дней текущего тарифа
                                                </Typography>
                                                <Typography
                                                    color="primary"
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    -
                                                    {formatNumber(
                                                        Math.round(period.priceWithSale - tariffUpgradeCharge)
                                                    )}{' '}
                                                    ₽
                                                </Typography>
                                            </Box>
                                        )}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={2}
                                        sx={{
                                            borderTop: '1px solid #E3ECF3',
                                        }}
                                    >
                                        <Typography
                                            color="primary"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 20,
                                            }}
                                        >
                                            Итого
                                        </Typography>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ minHeight: '32px' }}
                                        >
                                            {!isCustomPlanSelected &&
                                                selectedPlanObject &&
                                                !isDowngrade &&
                                                !isUpgrade &&
                                                total < selectedPlanObject.price * periodId && (
                                                    <Typography
                                                        variant="body1"
                                                        color="text.disabled"
                                                        sx={{ textDecoration: 'line-through', opacity: 0.7 }}
                                                    >
                                                        {formatNumber(selectedPlanObject.price * periodId)} ₽
                                                    </Typography>
                                                )}
                                            {isDowngrade && switchImmediately ? (
                                                <Typography
                                                    color="primary"
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: 20,
                                                        color: '#4AE287',
                                                    }}
                                                >
                                                    Бесплатно
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    color="primary"
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: 20,
                                                    }}
                                                >
                                                    {formatNumber(total)} ₽
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Выбор момента перехода на новый тариф (только если есть текущая подписка и выбран другой тариф) */}
                            {current_subscription &&
                                (isCustomPlanSelected ||
                                    (selectedPlanObject && selectedPlanObject.id !== current_subscription.plan.id)) && (
                                    <Box mb={3}>
                                        <Typography
                                            variant="body1"
                                            color="primary"
                                            sx={{ fontWeight: 600, mb: 1 }}
                                        >
                                            Когда перейти на новый тариф?
                                        </Typography>
                                        <Controller
                                            name="switch_immediately"
                                            control={control}
                                            render={({ field }) => (
                                                <Box
                                                    sx={{
                                                        border: '1px solid #E3ECF3',
                                                        borderRadius: '16px',
                                                        backgroundColor: '#fff',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                checked={field.value === true}
                                                                onChange={() => field.onChange(true)}
                                                                value={true}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Перейти сейчас
                                                                </Typography>
                                                                {isDowngrade && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', mt: 0.5 }}
                                                                    >
                                                                        Вы получите +
                                                                        {getWordWithNumber(tariffDowngradeRefund, [
                                                                            'день',
                                                                            'дня',
                                                                            'дней',
                                                                        ])}{' '}
                                                                        сервиса за оставшиеся дни
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        sx={{
                                                            px: 2,
                                                            py: 1.5,
                                                            width: '100%',
                                                            m: 0,
                                                            borderBottom: '1px solid #E3ECF3',
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                checked={field.value === false}
                                                                onChange={() => field.onChange(false)}
                                                                value={false}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Перейти после окончания текущего тарифа
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: 'rgba(0, 0, 0, 0.6)', mt: 0.5 }}
                                                                >
                                                                    Текущий тариф действует до{' '}
                                                                    {dayjs(current_subscription.end_date).format(
                                                                        'DD.MM.YYYY'
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        sx={{ px: 2, py: 1.5, width: '100%', m: 0 }}
                                                    />
                                                </Box>
                                            )}
                                        />
                                    </Box>
                                )}

                            {/* Автоматическое продление */}
                            <Box
                                mt={4}
                                mb={3}
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #E3ECF3',
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name="auto_renewal"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    color="primary"
                                                />
                                            )}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                color="primary"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Автоматическое продление тарифа
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                            >
                                                При включении автопродления вы получите 10% бонусами сразу после оплаты
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            {watch('auto_renewal') && (
                                <Box
                                    mt={2}
                                    mb={3}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: '#4AE287',
                                                opacity: 0.2,
                                                borderRadius: 4,
                                            }}
                                        />
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#000000',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                variant="body1"
                                            >
                                                💸 Вы получите {formatNumber(Math.round(total * 0.1))} ₽ бонусами сразу
                                                после оплаты
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    disabled={
                                        isCreatePaymentLoading ||
                                        (selectedTariffForPayment === null && selectedPlanId !== CUSTOM_PLAN_ID)
                                    }
                                >
                                    {isDowngrade && switchImmediately ? 'Перейти на новый тариф' : 'Перейти к оплате'}
                                </Button>
                                {watch('auto_renewal') && (
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                                    >
                                        Автопродление можно будет отключить в разделе «Ваш тариф»
                                    </Typography>
                                )}
                            </Box>
                        </form>
                    </Box>
                )}
            </Box>
        );
    };

    if (!isMobile) {
        return <DesktopLayout>{renderContent()}</DesktopLayout>;
    }

    return (
        <WithoutHeader
            showBack
            showMyHeader
            title="Ваш тариф"
        >
            {renderContent()}
        </WithoutHeader>
    );
};

export default MyTariffScene;
